/**
 * Per-purchaser workload, weighted by line items — the board this dashboard was asked for.
 *
 * The problem it solves: counting enquiries says a purchaser who closed one 130-line enquiry and
 * one who closed one 2-line enquiry did the same work. On live, `ENQ-2026-00024` carries 130 lines
 * while the other 22 enquiries carry 2 or fewer — one enquiry is 82% of every line item on the
 * system (measured 2026-08-09).
 *
 * So **Win% (enq) and Win% (line) sit side by side deliberately**. A wide gap between the two is
 * the finding: it means enquiry SIZE, not effort, is moving the numbers. Showing either column
 * alone would hide exactly that.
 *
 * Default sort is by lines descending, so the skew is the first thing on screen.
 */
import { useState } from 'react'
import type { PurchaserWorkload, Workload } from '../../lib/analytics/types'
import { BoardCard } from './BoardCard'
import { Track, pct } from './charts'

type SortKey = keyof PurchaserWorkload

const COLUMNS: ReadonlyArray<{ key: SortKey; label: string; numeric: boolean; title?: string }> = [
  { key: 'purchaser_name', label: 'Purchaser', numeric: false },
  { key: 'enquiries', label: 'Enquiries', numeric: true },
  { key: 'lines', label: 'Lines', numeric: true, title: 'Total enquiry line items handled' },
  { key: 'avg_lines_per_enquiry', label: 'Avg/enq', numeric: true, title: 'Average lines per enquiry' },
  { key: 'open_lines', label: 'Open lines', numeric: true, title: 'Lines still in flight' },
  { key: 'enquiries_won', label: 'Won enq', numeric: true },
  { key: 'lines_won', label: 'Won lines', numeric: true },
  { key: 'win_rate_enquiries', label: 'Win% (enq)', numeric: true, title: 'Share of enquiries won' },
  { key: 'win_rate_lines', label: 'Win% (line)', numeric: true, title: 'Share of LINE ITEMS won' },
  { key: 'avg_cycle_days', label: 'Avg cycle', numeric: true, title: 'Days from creation to last status change' },
]

/** Sort without mutating; stable, so equal values keep the order they arrived in. */
function sortRows(rows: readonly PurchaserWorkload[], key: SortKey, dir: 'asc' | 'desc') {
  const sign = dir === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => {
    const x = a[key]
    const y = b[key]
    if (typeof x === 'number' && typeof y === 'number') return (x - y) * sign
    return String(x).localeCompare(String(y)) * sign
  })
}

export function WorkloadBoard({
  workload, isLoading, error,
}: {
  workload: Workload | undefined
  isLoading: boolean
  error: unknown
}) {
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'lines', dir: 'desc' })
  const rows = workload?.purchasers ?? []
  const sorted = sortRows(rows, sort.key, sort.dir)
  const maxLines = rows.reduce((m, r) => Math.max(m, r.lines), 0)

  const toggle = (k: SortKey) =>
    setSort((s) => (s.key === k ? { key: k, dir: s.dir === 'desc' ? 'asc' : 'desc' } : { key: k, dir: 'desc' }))

  return (
    <BoardCard
      title="Purchaser workload — weighted by line items"
      subtitle="One enquiry of 130 lines is not one enquiry of 2. Sorted by lines, not enquiry count."
      isLoading={isLoading}
      error={error}
      isEmpty={rows.length === 0}
    >
      <div style={{ overflowX: 'auto' }}>
        <table style={{ marginTop: 10, width: '100%' }}>
          <thead>
            <tr>
              {COLUMNS.map((c) => (
                <th
                  key={String(c.key)}
                  className={c.numeric ? 'right' : undefined}
                  title={c.title}
                  style={{ cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none' }}
                  onClick={() => toggle(c.key)}
                >
                  {c.label}
                  {sort.key === c.key ? (sort.dir === 'desc' ? ' ▼' : ' ▲') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => (
              <tr key={r.purchaser}>
                <td title={r.purchaser}>{r.purchaser_name}</td>
                <td className="right mono">{r.enquiries}</td>
                <td className="right mono" style={{ whiteSpace: 'nowrap' }}>
                  {r.lines}
                  <Track value={r.lines} max={maxLines} tone="violet" />
                </td>
                <td className="right mono">{r.avg_lines_per_enquiry}</td>
                <td className="right mono">{r.open_lines}</td>
                <td className="right mono">{r.enquiries_won}</td>
                <td className="right mono">{r.lines_won}</td>
                <td className="right mono">{pct(r.win_rate_enquiries)}</td>
                <td className="right mono">{pct(r.win_rate_lines)}</td>
                <td className="right mono">{r.avg_cycle_days ? `${r.avg_cycle_days}d` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ color: 'var(--muted)', fontSize: 11.5, marginTop: 10 }}>
        <b>Win% (enq)</b> counts requirements closed; <b>Win% (line)</b> counts the items inside
        them. A wide gap between the two columns means enquiry size, not effort, is driving the
        numbers.
      </div>
    </BoardCard>
  )
}
